import { type FunctionComponent, Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { SidePanel, type SidePanelState } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { ErrorBlankTears } from '#shared/components/ErrorBlank';
import { ErrorBoundaryWrapper } from '#shared/components/ErrorBoundary';
import { PageStub } from '#shared/components/PageStub';
import { SidePanelAnimationContext } from '#shared/components/SidePanelAnimationContext';
import { type PreviewId, type PreviewType } from '#shared/lib/previewWidget';
import { preview } from './constants';
import styles from './SidePanelLayer.module.css';

export interface SidePanelLayerProps extends TestProps {
    previewId: PreviewId;
    data: unknown;
    isOpen: boolean;
    onClose: (id: PreviewId) => void;
}

export const SidePanelLayer: FunctionComponent<SidePanelLayerProps> = ({
    'data-testid': dataTestId = 'SidePanelLayer',
    previewId,
    data,
    isOpen,
    onClose,
}) => {
    const refPreviewData = useRef<unknown>();

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    refPreviewData.current = data;

    const { type, id } = useMemo(() => {
        if (previewId) {
            const [type, id = null] = previewId.split('/') ?? [null, null];

            if (type in preview) {
                return { type: type as PreviewType, id };
            }
        }

        return { type: null, id: null };
    }, [previewId]);

    const content = useMemo(() => {
        if (id && type) {
            const Preview = preview[type].Preview;

            return (
                <Suspense fallback={<PageStub />}>
                    {/* eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723 */}
                    <Preview id={id} initialPreviewData={refPreviewData.current} />
                </Suspense>
            );
        }

        return <ErrorBlankTears title="Ничего не найдено для превью" />;
    }, [type, id]);

    const buttons = useMemo(() => {
        if (type && id) {
            const Buttons = preview[type].Buttons;

            if (Buttons) {
                return (
                    <Suspense fallback={null}>
                        {/* eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723 */}
                        <Buttons id={id} initialPreviewData={refPreviewData.current} />
                    </Suspense>
                );
            }
        }

        return null;
    }, [id, type]);

    const handleClose = useCallback(() => {
        onClose(previewId);
    }, [onClose, previewId]);

    const [animationEnd, setAnimationEnd] = useState<SidePanelState>('close');
    const onAnimationEnd = useCallback<(animationState: SidePanelState) => void>((state) => {
        setAnimationEnd(state);
    }, []);

    return (
        <SidePanel
            data-testid={dataTestId}
            isOpen={isOpen}
            width={type && preview[type] ? preview[type].sidePanelWidth : 's'}
            onClose={handleClose}
            dismissOnOutsideClick={
                type && preview[type]?.dismissOnOutsideClick === undefined
                    ? true
                    : Boolean(type && preview[type]?.dismissOnOutsideClick)
            }
            bottomButtons={buttons}
            className={(type ? preview[type]?.className : undefined) ?? styles.root}
            onAnimationEnd={onAnimationEnd}
        >
            <ErrorBoundaryWrapper>
                <SidePanelAnimationContext.Provider value={{ animationEnd }}>
                    {content}
                </SidePanelAnimationContext.Provider>
            </ErrorBoundaryWrapper>
        </SidePanel>
    );
};
